import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Calculator, Leaf, Loader2 } from 'lucide-react';
import { Slider } from '../ui/slider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Defaults
const DEFAULT_BETRIEBSSTUNDEN = 6000;
const DEFAULT_LECKAGEN = 20;
const DEFAULT_LECKGROESSE_MM = 2;
const DEFAULT_STROMPREIS_EUR_KWH = 0.18;
const DEFAULT_DRUCK_BAR = 7;

// Slider ranges
const MIN_BETRIEBSSTUNDEN = 1000;
const HOURS_PER_YEAR = 8760; // 24 × 365

function fmtNum(n, decimals = 0) {
  if (n === null || n === undefined || isNaN(n)) return '–';
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: decimals, minimumFractionDigits: decimals }).format(n);
}

function fmtEuro(n) {
  return fmtNum(n, 0) + ' €';
}

function Field({ label, value, unit, min, max, step, onChange, testId }) {
  const sliderValue = useMemo(() => [value], [value]);
  return (
    <div className="py-5 border-b border-slate-200/70">
      <div className="flex items-center justify-between mb-3">
        <label className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</label>
        <div className="font-mono-tech text-sm text-[#0891b2] tabular-nums">
          {fmtNum(value, step < 1 ? 2 : 0)} <span className="text-slate-500">{unit}</span>
        </div>
      </div>
      <Slider
        data-testid={testId}
        value={sliderValue}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
      />
      <div className="flex justify-between mt-1.5 font-mono-tech text-[10px] text-slate-400">
        <span>{min}</span><span>{max}</span>
      </div>
    </div>
  );
}

export default function ROICalculator() {
  const [betrieb, setBetrieb] = useState(DEFAULT_BETRIEBSSTUNDEN);
  const [lecks, setLecks] = useState(DEFAULT_LECKAGEN);
  const [groesse, setGroesse] = useState(DEFAULT_LECKGROESSE_MM);
  const [strom, setStrom] = useState(DEFAULT_STROMPREIS_EUR_KWH);
  const [druck, setDruck] = useState(DEFAULT_DRUCK_BAR);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const params = useMemo(() => ({
    betriebsstunden_jahr: betrieb,
    leckagen_anzahl: lecks,
    leckage_groesse_mm: groesse,
    strompreis_kwh: strom,
    druck_bar: druck,
  }), [betrieb, lecks, groesse, strom, druck]);

  const onCalc = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(`${API}/roi/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      if (!res.ok) throw new Error('Berechnung fehlgeschlagen');
      const data = await res.json();
      setResult(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [params]);

  // Initial calc on mount only — subsequent calcs are user-triggered via the button.
  useEffect(() => {
    onCalc();
  }, []);

  return (
    <section id="roi" className="relative py-16 sm:py-24 md:py-32" data-testid="roi-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <div className="eyebrow mb-5">
            <Calculator className="w-3.5 h-3.5" /> ROI-Rechner
          </div>
          <h2 className="font-display font-black text-slate-900 text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-[1.02]">
            Was kostet Sie Ihr<br />
            <span className="text-[#0891b2]">Druckluftleck wirklich?</span>
          </h2>
          <p className="mt-5 text-slate-600 text-base">
            Konkrete Zahlen statt Schätzungen. Geben Sie Ihre Anlagendaten ein und sehen Sie Ihr 
            jährliches Einsparpotenzial in Euro – live berechnet.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 card-surface p-6 sm:p-7 md:p-10" data-testid="roi-calculator-form">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
              Eingabe · Anlagendaten
            </div>
            <Field label="Betriebsstunden / Jahr" value={betrieb} unit="h" min={MIN_BETRIEBSSTUNDEN} max={HOURS_PER_YEAR} step={100} onChange={setBetrieb} testId="slider-betrieb" />
            <Field label="Anzahl Lecks (geschätzt)" value={lecks} unit="" min={0} max={200} step={1} onChange={setLecks} testId="slider-lecks" />
            <Field label="Ø Leckgröße" value={groesse} unit="mm" min={0.5} max={10} step={0.1} onChange={setGroesse} testId="slider-groesse" />
            <Field label="Strompreis" value={strom} unit="€/kWh" min={0.05} max={0.6} step={0.01} onChange={setStrom} testId="slider-strom" />
            <Field label="Systemdruck" value={druck} unit="bar" min={4} max={12} step={0.5} onChange={setDruck} testId="slider-druck" />

            <button
              className="btn-primary w-full justify-center mt-7"
              onClick={onCalc}
              disabled={loading}
              data-testid="roi-calculate-button"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Berechnung…</> : 'Live berechnen'}
            </button>
            {err && <div className="text-[#ff2a55] text-sm mt-3 font-mono-tech">{err}</div>}
          </div>

          <div className="lg:col-span-5 space-y-4">
            <div className="card-surface p-5 sm:p-7" data-testid="roi-result-main">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                Ihre jährlichen Verluste
              </div>
              <div className="font-display font-black text-[#ff2a55] tabular-nums text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-none" data-testid="roi-result-value">
                {result ? fmtEuro(result.kosten_euro_pro_jahr) : '–'}
              </div>
              <div className="text-slate-500 text-sm mt-2">pro Jahr – ungenutzt verpufft</div>

              <div className="mt-6 pt-6 border-t border-slate-200/70 space-y-3">
                <Row label="Verlust Druckluft" value={result ? `${fmtNum(result.verlust_m3_pro_jahr)} m³/Jahr` : '–'} />
                <Row label="Strom-Energie" value={result ? `${fmtNum(result.verlust_kwh_pro_jahr)} kWh/Jahr` : '–'} />
                <Row label="CO₂ Emission" value={result ? `${fmtNum(result.co2_kg_pro_jahr)} kg/Jahr` : '–'} icon={Leaf} color="#16a34a" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400">
          * Berechnung basiert auf branchenüblichen Richtwerten (0,12 kWh/m³, 0,4 kg CO₂/kWh). 
          Exakte Werte erhalten Sie im persönlichen Audit.
        </div>
      </div>
    </section>
  );
}

function Row({ label, value, icon: Icon, color = '#0f172a' }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2 text-slate-600">
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
        {label}
      </div>
      <div className="font-mono-tech tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
